import { UseFormReturn } from "react-hook-form";

type CoreData = {
  countryList: Array<{ Value: string; Text: string }>;
  states: Array<{ Code: string; Name: string }>;
  raceList: Array<{ Value: string; Text: string }>;
  languagesList: Array<{ Value: string; Text: string }>;
  IEPorIFSPList?: Array<{ Value: string; Text: string }>;
  calWORKsList?: Array<{ Value: string; Text: string }>;
  educationList?: Array<{ Value: string; Text: string }>;
  reasonForServiceList?: Array<{ Value: string; Text: string }>;
};

export function useReviewFieldTransformers(
  coreData: CoreData,
  familyForm?: UseFormReturn<any>,
  childForm?: UseFormReturn<any>
) {
  // ==================== BASIC TRANSFORMERS ====================

  const getYesNoText = (value: string | null | undefined): string => {
    if (value === null || value === undefined || value === "__UNSET__")
      return "—";
    if (value === "Y") return "Yes";
    if (value === "N") return "No";
    if (value === "") return "No Response";
    return value;
  };

  // ==================== GEOGRAPHY TRANSFORMERS ====================

  const getCountryText = (countryCode: string | null | undefined): string => {
    if (!countryCode) return "—";
    const country = coreData.countryList.find((c) => c.Value === countryCode);
    return country?.Text || countryCode;
  };

  const getStateText = (stateCode: string | null | undefined): string => {
    if (!stateCode) return "—";
    const state = coreData.states.find((s) => s.Code === stateCode);
    return state?.Name || stateCode;
  };

  // ==================== CHILD INFORMATION TRANSFORMERS ====================

  const getGenderText = (genderCode: string | null | undefined): string => {
    if (!genderCode) return "—";
    const genderMap: Record<string, string> = {
      M: "Male",
      F: "Female",
      X: "Nonbinary",
    };
    return genderMap[genderCode] || genderCode;
  };

  const getIEPIFSPText = (iepCode: string | null | undefined): string => {
    if (!iepCode) return "—";
    const iep = coreData.IEPorIFSPList?.find((i: any) => i.Value === iepCode);
    return iep?.Text || iepCode;
  };

  const getAllRacesText = (): string => {
    if (!childForm) return "—";

    const races = [
      childForm.watch("ChildRace1"),
      childForm.watch("ChildRace2"),
      childForm.watch("ChildRace3"),
      childForm.watch("ChildRace4"),
      childForm.watch("ChildRace5"),
    ].filter(Boolean);

    if (races.length === 0) return "—";

    return races
      .map((raceCode) => {
        const race = coreData.raceList.find((r) => r.Value === raceCode);
        return race?.Text || raceCode;
      })
      .join(", ");
  };

  // ==================== FAMILY INFORMATION TRANSFORMERS ====================

  const getCalWorksText = (calWorksCode: string | null | undefined): string => {
    if (!calWorksCode) return "—";
    const calWorks = coreData.calWORKsList?.find(
      (c: any) => c.Value === calWorksCode
    );
    return calWorks?.Text || calWorksCode;
  };

  const getEducationText = (
    educationCode: string | null | undefined
  ): string => {
    if (!educationCode) return "—";
    const education = coreData.educationList?.find(
      (e: any) => e.Value === educationCode
    );
    return education?.Text || educationCode;
  };

  const getServiceReasonsText = (
    hohPrefix: "HeadOfHousehold1" | "HeadOfHousehold2"
  ): string => {
    if (!familyForm) return "—";

    const formValues = familyForm.getValues();
    const reasons = [
      formValues[`${hohPrefix}NeedForService1`],
      formValues[`${hohPrefix}NeedForService2`],
      formValues[`${hohPrefix}NeedForService3`],
      formValues[`${hohPrefix}NeedForService4`],
      formValues[`${hohPrefix}NeedForService5`],
      formValues[`${hohPrefix}NeedForService6`],
      formValues[`${hohPrefix}NeedForService7`],
      formValues[`${hohPrefix}NeedForService8`],
      formValues[`${hohPrefix}NeedForService9`],
    ].filter(Boolean);

    if (reasons.length === 0) return "—";

    return reasons
      .map((reasonCode) => {
        const reason = coreData.reasonForServiceList?.find(
          (r: any) => r.Value === reasonCode
        );
        return reason?.Text || reasonCode;
      })
      .join(", ");
  };

  // ==================== LANGUAGE TRANSFORMERS ====================

  const getLanguageText = (languageCode: string | null | undefined): string => {
    if (!languageCode) return "—";
    const language = coreData.languagesList?.find(
      (l: any) => l.Value === languageCode
    );
    return language?.Text || languageCode;
  };

  const getMultiLanguagesText = (
    languageCodes: (string | null | undefined)[]
  ): string => {
    const validCodes = languageCodes.filter(Boolean);
    if (validCodes.length === 0) return "—";

    return validCodes
      .map((code) => {
        const language = coreData.languagesList?.find(
          (l: any) => l.Value === code
        );
        return language?.Text || code;
      })
      .join(", ");
  };

  // ==================== ENROLLMENT TRANSFORMERS ====================

  const getContractTypeText = (
    contractCode: string | null | undefined
  ): string => {
    if (!contractCode) return "—";
    const contract = (coreData as any).contractTypesOfferedList?.find(
      (c: any) => c.Value === contractCode
    );
    return contract?.Text || contractCode;
  };

  const getProgramTypeText = (
    programCode: string | null | undefined
  ): string => {
    if (!programCode) return "—";
    const program = (coreData as any).programTypeList?.find(
      (p: any) => p.Value === programCode
    );
    return program?.Text || programCode;
  };

  const getProgramDurationText = (
    durationCode: string | null | undefined
  ): string => {
    if (!durationCode) return "—";
    const duration = (coreData as any).programDurationList?.find(
      (d: any) => d.Value === durationCode
    );
    return duration?.Text || durationCode;
  };

  const getEligibilityStatusText = (
    statusCode: string | null | undefined
  ): string => {
    if (!statusCode) return "—";
    const status = (coreData as any).eligibilityStatusList?.find(
      (e: any) => e.Value === statusCode
    );
    return status?.Text || statusCode;
  };

  const getExitReasonText = (exitCode: string | null | undefined): string => {
    if (!exitCode) return "—";
    const exitReason = (coreData as any).studentExitCategoryList?.find(
      (e: any) => e.Value === exitCode
    );
    return exitReason?.Text || exitCode;
  };

  // ==================== RETURN ALL TRANSFORMERS ====================

  return {
    // Basic
    getYesNoText,

    // Geography
    getCountryText,
    getStateText,

    // Child Information
    getGenderText,
    getIEPIFSPText,
    getAllRacesText,

    // Family Information
    getCalWorksText,
    getEducationText,
    getServiceReasonsText,

    // Language
    getLanguageText,
    getMultiLanguagesText,

    // Enrollment
    getContractTypeText,
    getProgramTypeText,
    getProgramDurationText,
    getEligibilityStatusText,
    getExitReasonText,
  };
}
